/**
 * One benefit card in the mobile (below `lg`) "Value-Added Benefits" section;
 * `../ValueAddedBenefits.tsx` renders one per benefit. A coloured box with the title and
 * description, and above them the image when `showImageOnMobile` is set. The image overlaps the
 * box's top edge and alternates sides: flush left on even positions, inset 20 px on odd ones.
 */
import React from "react";
import styled from "@emotion/styled";
import { Flex } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import { SecondaryContent } from "../ValueAddedBenefits";

interface ValueAddedBenefitsMobileProps {
	/** Card background colour. */
	backgroundColor: string;
	/** The card's position in the list; even and odd positions place the image differently. */
	id: number;
	/** Description text colour. */
	textColor: string;
	content: SecondaryContent;
}

/** Renders one mobile benefit card; see the file header. */
const ValueAddedBenefitsMobile: React.FC<ValueAddedBenefitsMobileProps> = ({
	backgroundColor,
	id,
	textColor,
	content,
}) => {
	const { theme } = useTheme();

	const ContentDiv = styled.div`
		margin-top: 65px;
	`;

	const MobileImageContainer = styled.div`
		position: relative;
		top: -40px;
		display: flex;
		justify-content: ${id % 2 === 0 ? "flex-start" : "flex-end"};
	`;

	const ContentWrapper = styled.div`
		margin-top: -35px;
		padding: 0 20px 25px 20px;
	`;

	const imageStyle = css`
		width: calc(100% - 20px);
		height: auto;
		display: block;
		margin-left: ${id % 2 === 0 ? "0px" : "20px"};
	`;

	return (
		<>
			<ContentDiv css={{ backgroundColor: backgroundColor }}>
				<Flex direction="column">
					{content.showImageOnMobile ? (
						<MobileImageContainer>
							<img src={content.imageUrl} css={imageStyle} alt={content.title} />
						</MobileImageContainer>
					) : (
						<span></span>
					)}
					<ContentWrapper>
						<Typography variant="h3" color={theme.colors.primary.white}>
							{content.title}
						</Typography>
						<Typography color={textColor}>{content.description}</Typography>
					</ContentWrapper>
				</Flex>
			</ContentDiv>
		</>
	);
};

export default ValueAddedBenefitsMobile;
